import type { ScanResult } from './types';

/**
 * The stable public interface, exposed as window.FP_SCAN.
 *
 * Parity note: the reference site exposes window.FINGERPRINT_SCAN so agents and
 * automation can read the scan without parsing the DOM. We expose the same
 * shape under window.FP_SCAN (and mirror the name FINGERPRINT_SCAN as an alias
 * for drop-in comparison).
 *
 * We deliberately do NOT expose rule thresholds or the reference datasets here,
 * so the object is useful for debugging without reading like a bypass guide.
 */

export interface PublicScan {
  status: ScanResult['status'];
  fingerprintHash: string;
  fingerprintId: string;
  fingerprintParts: ScanResult['fingerprintParts'];
  botScore: number;
  reasons: ScanResult['reasons'];
  signals: ScanResult['signals'];
  findings: Array<Omit<ScanResult['findings'][number], 'scoreContribution'>>;
  crossContext: ScanResult['crossContext'];
  startedAt: number;
  finishedAt: number;
  rerun(): Promise<PublicScan>;
  exportJson(): string;
  compareTo(previous: PublicScan): Record<string, { before: unknown; after: unknown }>;
}

declare global {
  interface Window {
    FP_SCAN?: PublicScan;
    FINGERPRINT_SCAN?: PublicScan;
  }
}

/** Strip scoreContribution and internals from findings for the public surface. */
function toPublic(result: ScanResult): PublicScan {
  return {
    status: result.status,
    fingerprintHash: result.fingerprintHash,
    fingerprintId: result.fingerprintId,
    fingerprintParts: result.fingerprintParts,
    botScore: result.botScore,
    reasons: result.reasons,
    signals: result.signals,
    findings: result.findings.map(({ scoreContribution: _omit, ...rest }) => rest),
    crossContext: result.crossContext,
    startedAt: result.startedAt,
    finishedAt: result.finishedAt,
    rerun: async () => toPublic(await rerunCallback()),
    exportJson: () => JSON.stringify(result, null, 2),
    compareTo: (previous: PublicScan) => compare(previous, toPublic(result)),
  };
}

let rerunCallback: () => Promise<ScanResult> = async () => {
  throw new Error('rerun not wired');
};

/** Wire the internal rerun used by the public object's rerun(). */
export function wireRerun(fn: () => Promise<ScanResult>): void {
  rerunCallback = fn;
}

export function publishScan(result: ScanResult): PublicScan {
  const pub = toPublic(result);
  if (typeof window !== 'undefined') {
    window.FP_SCAN = pub;
    window.FINGERPRINT_SCAN = pub; // parity alias
  }
  return pub;
}

function compare(
  before: PublicScan,
  after: PublicScan,
): Record<string, { before: unknown; after: unknown }> {
  const diff: Record<string, { before: unknown; after: unknown }> = {};
  for (const part of Object.keys(after.fingerprintParts)) {
    const b = before.fingerprintParts[part as keyof typeof before.fingerprintParts];
    const a = after.fingerprintParts[part as keyof typeof after.fingerprintParts];
    if (b !== a) diff[`part.${part}`] = { before: b, after: a };
  }
  if (before.fingerprintHash !== after.fingerprintHash) {
    diff.fingerprintHash = { before: before.fingerprintHash, after: after.fingerprintHash };
  }
  return diff;
}
